<script lang="ts" setup>
import BigButton from "~/components/sx/components/BigButton.vue";
import {loadImage} from "~/helpers/imageCache";
import IconTypeMobile from "~/components/sx/icons/types/IconTypeMobile.vue";
import IconTypeResident from "~/components/sx/icons/types/IconTypeResident.vue";
import IconTypeCorporate from "~/components/sx/icons/types/IconTypeCorporate.vue";
import {useWindowSize} from "@vueuse/core";
import TypesMobile from "~/components/sx/types/TypesMobile.vue";

type IVideo = null | HTMLVideoElement;

const { width } = useWindowSize();
const { t } = useI18n();
const config = useRuntimeConfig();
const { locale: localeI18n } = useI18n();

withDefaults(defineProps<{ centered?: boolean }>(), { centered: true });

const src = ref<string | null>(null);

const types = shallowRef([
  {
    id: 1,
    nameKey: 'pages.home.types.mobile.name',
    descriptionKey: 'pages.home.types.mobile.description',
    type: 'mobile',
    ips: '4 403 554 IPs',
    price: '$4.5/GB',
    video: 'NewMobile.webm',
    icon: markRaw(IconTypeMobile),
  },
  {
    id: 2,
    nameKey: 'pages.home.types.resident.name',
    descriptionKey: 'pages.home.types.resident.description',
    type: 'resident',
    ips: '7 034 655 IPs',
    price: '$4.13/GB',
    video: 'NewResident.webm',
    icon: markRaw(IconTypeResident),
  },
  {
    id: 3,
    nameKey: 'pages.home.types.corporate.name',
    descriptionKey: 'pages.home.types.corporate.description',
    type: 'corporate',
    ips: '1 043 554 IPs',
    price: '$0.7/GB',
    video: 'NewCorporate.webm',
    icon: markRaw(IconTypeCorporate),
  },
]);

const videoRefs = ref<IVideo[]>([])

function setVideoRef(el: IVideo, index: number) {
  if (el) videoRefs.value[index] = el
}

const handleMouseEnter = (index: number) => {
  if (videoRefs.value[index]) {
    videoRefs.value[index]?.play();
  }
}

onMounted(async () => {
  src.value = await loadImage('/sx/Types.webp');
});
</script>

<template>
  <section class="sx-proxy-types" :class="{ start: !centered }">
    <div v-if="centered" class="sx-proxy-types__title">
      <p class="sx-landing-title">
        {{ $t('pages.home.types.title1') }}
      </p>
      <p class="sx-landing-title">
        {{ $t('pages.home.types.title2') }}
      </p>
    </div>
    <div v-else class="sx-proxy-types__title">
      <h1 class="sx-landing-title">
        {{ $t('pages.home.hero.title') }}
      </h1>
      <p class="sx-proxy-types__desc">
        {{ $t('pages.home.types.subtitle') }}
      </p>
    </div>

    <div v-if="width > 767" class="sx-proxy-types__info">
      <div
        v-for="(type, index) in types"
        :key="type.id"
        class="sx-proxy-types__item"
        @mouseenter="handleMouseEnter(index)"
      >
        <NuxtLink
          :to="`/${localeI18n}/proxy/${type.type}`"
          style="color: #000"
          class="sx-proxy-types__link"
        >
          <div class="sx-proxy-types__video-wrapper">
            <video
              :ref="el => setVideoRef(el, index)"
              muted
              autoplay
              playsinline
              class="sx-proxy-types__video"
            >
              <source :src="`/video/${type.video}`" type="video/webm" />
            </video>
          </div>
          <div class="sx-proxy-types__text">
            <p class="sx-proxy-types__item-title">
              {{ $t(type.nameKey) }}
            </p>
            <p class="sx-proxy-types__item-desc">
              {{ type.ips }} {{ $t('pages.home.types.from') }} {{ type.price }}
            </p>
            <p class="sx-proxy-types__item-desc">
              {{ $t(type.descriptionKey) }}
            </p>
          </div>
        </NuxtLink>
      </div>
    </div>
    <TypesMobile v-else :types="types" />
    <BigButton
      class="sx-proxy-types__button"
      :text="$t('pages.home.types.tryFree')"
      :to="config.public.signupUrl"
    />
  </section>
</template>

<style lang="scss" scoped>
.sx-proxy-types {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 48px 0 60px;
  min-height: 100vh;
  z-index: 999;
  position: relative;
  background: #fff;
  @media (max-width: 600px) {
    padding: 2rem 1rem;
  }

  &.start {
    justify-content: flex-start;
    z-index: 1;
    padding-top: 0;
    height: auto;
  }
  &__img {
    width: 100%;
    margin-top: 24px;
    img {
      width: 100%;
    }
  }
  &__video-wrapper {
    overflow: hidden;
  }
  &__video {
    width: 100% !important;
    height: 100% !important;
    scale: 1.175;
    object-fit: contain;
  }
  &__desc {
    margin-top: 20px;
    color: #000;
    font-size: 24px;
    font-weight: 400;
    line-height: 32px;
  }

  &__button {
    margin-top: 52px;
  }

  &__transform {
    transform: translateY(-40px);
    justify-content: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    margin-bottom: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
    text-align: center;
    @media (max-width: 600px) {
      margin-bottom: 16px;
    }
  }

  &__video-wrapper {
    position: relative;
  }

  &__video-line {
    position: absolute;
    z-index: 999;
    height: 100%;
    width: 5px;
    background: #ffffff;

    &--right {
      top: 0;
      right: -1px;
    }
  }

  @media (width < 1040px) {
    &__info {
      flex-wrap: wrap;
      gap: 24px !important;
    }

    &__item {
      flex-shrink: 0;
      gap: 0 !important;
    }
  }

  &__info {
    margin-top: 16px;
    width: 96%;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-around;
    gap: 0;
    z-index: 5;
    @media (max-width: 600px) {
      gap: 12px !important;
    }
  }

  &__link {
    color: #000;
  }
  &__text {
    padding: 32px;
    border-radius: 16px;
    text-align: start;
  }
  &__item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 364px;
    width: 33.33%;
    border-radius: 20px;
    transition: .3s;
    min-height: 475px;
    text-align: center;
    cursor: pointer;
    @media (max-width: 899px) {
      padding: 1rem;
      width: 100%;
      max-width: 60vw;
      min-height: auto;
    }
    @media (max-width: 599px) {
      max-width: 80vw;
      border-bottom: var(--border-sx-main);
      border-radius: 0;
    }
    &:hover {
      .sx-proxy-types__text {
        background: var(--bg-sx-gray);
      }
    }

    &:not(:first-child) {
      transform: translateX(0);
    }

    &-title {
      z-index: 999;
      font-size: 40px;
      line-height: 44px;
      letter-spacing: -0.03em;
      color: #191C1F;

      @media (max-width: 600px) {
        font-size: 24px;
      }
    }

    &-desc {
      margin-top: 16px;
      font-weight: 400;
      font-size: 18px;
      line-height: 120%;
      letter-spacing: 0;
      @media (max-width: 600px) {
        margin-top: 4px;
      }
    }
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    max-width: 250px;
    padding: 0 24px;
    border-radius: 30px;
    background: #000;
    color: #fff;
    position: relative;
    overflow: hidden;
    margin-block-start: 48px;
    font-size: 18px;

    @media (min-width: 480px) {
      margin-block-start: 0;
    }

    &:hover {
      background: #fff;
      outline: 1px solid #000;
      color: #000;
    }
  }
}


</style>
